import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { EMPTY, Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { ToastGlobalService } from 'src/app/layout/toast-global/toast-global.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class HttpInterceptorService implements HttpInterceptor {

  constructor(
    private readonly auth: AuthService,
    private readonly toast: ToastGlobalService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only attach Bearer + 401 recovery for our own API; third-party
    // requests (CDN assets, public endpoints) pass through unmodified.
    if (req.url.indexOf(environment.apiServer) === -1) {
      return next.handle(req);
    }

    return next.handle(this.withAuth(req)).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) return this.recoverIfSession(req, next, err);
        this.report(err);
        return throwError(() => err);
      })
    );
  }

  // A 401 means "session expired" only if there was a session. A visitor who
  // never signed in (invite registration, /404, /unauthorized) gets the 401
  // handed back to the caller; sending them to sign-in would replace the page
  // they came to see.
  private recoverIfSession(req: HttpRequest<any>, next: HttpHandler, err: HttpErrorResponse): Observable<HttpEvent<any>> {
    return from(this.auth.hasSession()).pipe(
      switchMap(hasSession => {
        if (hasSession) return this.recover(req, next);
        this.report(err);
        return throwError(() => err);
      })
    );
  }

  // 401 recovery: one renew attempt, retry the original request with the
  // fresh token. The renew itself is single-flighted by AuthService so a
  // wave of concurrent 401s only triggers one signinSilent.
  //
  // catchError is positioned BEFORE the switchMap so it only catches
  // renew rejections — that's the "session is dead, go re-auth" case.
  // Errors from the retried request (500, network drop, unexpected 4xx)
  // bubble through to the original caller, which is the right place to
  // surface them. Catching after switchMap would treat any retry-side
  // failure as a dead-session signal and silently redirect to IdP.
  private recover(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this.auth.renewToken()).pipe(
      catchError(() => {
        // Renew failed — refresh_token is dead. Navigate to login and
        // complete the observable silently; callers that need to react
        // to session loss should subscribe to user$.
        this.auth.startAuthentication();
        return EMPTY;
      }),
      switchMap(() => next.handle(this.withAuth(req)))
    );
  }

  private withAuth(req: HttpRequest<any>): HttpRequest<any> {
    const headers: Record<string, string> = {};
    // No token, no header: an empty "Bearer " is a malformed credential, not
    // an anonymous request.
    const token = this.auth.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Skip Content-Type when the caller set enctype (file upload /
    // multipart) — Angular sets the right boundary automatically.
    if (!req.headers.has('enctype')) {
      headers['Content-Type'] = 'application/json';
    }
    return req.clone({ setHeaders: headers });
  }

  private report(err: HttpErrorResponse): void {
    // Status 0 means the request never reached the server — almost always
    // a navigation-cancelled XHR (logout, full re-auth, browser back),
    // occasionally a CORS or network drop. None of these are actionable
    // for the user, so we skip BOTH the dev toast and the console.error
    // — without this, every logout produces a toast and a noisy stack
    // trace for each in-flight request that was cancelled by the redirect.
    if (err.status === 0) return;
    const message = err.error instanceof ErrorEvent
      ? `Error: ${err.error.message}`
      : `Error Status ${err.status}: ${err.message}`;
    const env = environment.apiServer;
    if (env.includes('dev') || env.includes('localhost')) {
      this.toast.show(message, { classname: 'error' });
    }
    console.error(message);
  }
}
